/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Search,
  Mail,
  Send,
  CheckCircle,
  X,
  ChevronDown,
  MessageSquareText,
  Phone,
  Eye,
  EyeOff,
  Bold,
  Italic,
  Underline,
  List,
  Link as LinkIcon,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Filter,
  RefreshCw,
} from "lucide-react";
import Pagination from "../../../components/Pagination";
import { AdminContext } from "../../../context/AdminContext";
import { toast } from "react-toastify";

const ContactUs = () => {
  const {
    contactForms,
    contactPagination,
    fetchAllContactForms,
    markContactRead,
    markContactReplied,
  } = useContext(AdminContext);

  // Data State
  const [submissions, setSubmissions] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  // UI State
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchInput, setSearchInput] = useState(""); // What user types
  const [searchTerm, setSearchTerm] = useState(""); // What actually triggers the search
  const [statusFilter, setStatusFilter] = useState("All");
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const statusDropdownRef = useRef(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Email Modal State
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const textareaRef = React.useRef(null);

  // Rich text editor functions
  const applyFormatting = (command, value = null) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = emailBody.substring(start, end);

    if (!selectedText) {
      toast.info("Please select text to format");
      return;
    }

    let formattedText = selectedText;
    let newBody = emailBody;

    switch (command) {
      case "bold":
        formattedText = `**${selectedText}**`;
        break;
      case "italic":
        formattedText = `*${selectedText}*`;
        break;
      case "underline":
        formattedText = `__${selectedText}__`;
        break;
      case "link":
        const url = prompt("Enter URL:");
        if (url) {
          formattedText = `[${selectedText}](${url})`;
        } else {
          return;
        }
        break;
      case "list":
        formattedText = `\n• ${selectedText}`;
        break;
      default:
        return;
    }

    newBody =
      emailBody.substring(0, start) + formattedText + emailBody.substring(end);
    setEmailBody(newBody);

    // Restore focus and set cursor position
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + formattedText.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  // Fetch data with filters
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Build filter params
        const params = {
          page: currentPage,
          limit: itemsPerPage,
        };

        //  search term if provided
        if (searchTerm.trim()) {
          params.search = searchTerm;
        }

        // status filter (convert "All" to empty string or null)
        if (statusFilter !== "All") {
          params.status = statusFilter;
        }

        await fetchAllContactForms(params);
      } catch (error) {
        console.error("Error fetching contact forms:", error);
        toast.error("Failed to fetch contact forms");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [currentPage, itemsPerPage, searchTerm, statusFilter]);

  // Update submissions when contactForms data changes
  useEffect(() => {
    // Check if contactForms exists and is an array
    if (!contactForms || !Array.isArray(contactForms)) {
      setSubmissions([]);
      return;
    }

    const mappedSubmissions = contactForms.map((form) => ({
      id: form.id,
      name: `${form.firstName || ""} ${form.lastName || ""}`.trim(),
      initial: form.firstName?.charAt(0)?.toUpperCase() || "U",
      email: form.email || "",
      phone: form.phone || "",
      subject: form.subject || "",
      message: form.message || "",
      date: form.createdAt ? new Date(form.createdAt).toLocaleDateString() : "",
      status: form.status || "New",
    }));

    setSubmissions(mappedSubmissions);
  }, [contactForms]);

  // Use backend pagination data
  const paginatedItems = submissions;
  const totalPages = contactPagination?.totalPages || 1;
  const totalItems = contactPagination?.totalItems || 0;

  // Handlers

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const pageIds = paginatedItems.map((s) => s.id);
      setSelectedIds((prev) => new Set([...prev, ...pageIds]));
    } else {
      const pageIds = new Set(paginatedItems.map((s) => s.id));
      setSelectedIds((prev) => {
        const next = new Set(prev);
        for (const id of pageIds) next.delete(id);
        return next;
      });
    }
  };

  const handleSelectOne = (id, e) => {
    // Prevent event bubbling
    if (e) {
      e.stopPropagation();
    }

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleMessage = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleMarkAsRead = async (id) => {
    try {
      await markContactRead(id);
      toast.success("Marked as read");

      // Update local state immediately
      setSubmissions((prev) =>
        prev.map((sub) => (sub.id === id ? { ...sub, status: "Read" } : sub)),
      );

      // Refresh data once after a short delay to ensure backend is updated
      setTimeout(() => {
        refreshDataSilently();
      }, 300);
    } catch (error) {
      toast.error("Failed to mark as read");
    }
  };

  const handleOpenEmailModal = () => {
    if (selectedIds.size === 0) return;
    setEmailSubject("");
    setEmailBody("");
    setEmailError("");
    setShowEmailModal(true);
  };

  const handleSendEmail = async () => {
    if (!emailSubject.trim()) {
      setEmailError("Subject is required.");
      return;
    }
    if (!emailBody.trim()) {
      setEmailError("Message content is required.");
      return;
    }

    setIsSending(true);

    try {
      const idsArray = Array.from(selectedIds);

      // Simulate sending email first
      setTimeout(async () => {
        try {
          // Mark as replied in the backend
          await markContactReplied(idsArray);

          // Update local state for selected items
          setSubmissions((prev) =>
            prev.map((sub) =>
              selectedIds.has(sub.id) ? { ...sub, status: "Replied" } : sub,
            ),
          );

          setIsSending(false);
          setShowEmailModal(false);
          setShowSuccess(true);

          setSelectedIds(new Set());
          setTimeout(() => setShowSuccess(false), 3000);

          toast.success("Email sent and marked as replied!");

          // Refresh data once after a short delay
          setTimeout(() => {
            refreshDataSilently();
          }, 300);
        } catch (error) {
          setIsSending(false);
          toast.error("Failed to mark as replied");
        }
      }, 1500);
    } catch (error) {
      setIsSending(false);
      toast.error("Failed to send email");
    }
  };

  const handleRefresh = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (searchTerm.trim()) {
        params.search = searchTerm;
      }

      if (statusFilter !== "All") {
        params.status = statusFilter;
      }

      await fetchAllContactForms(params);
    } catch (error) {
      toast.error("Failed to refresh data");
    } finally {
      setIsLoading(false);
    }
  };

  //  refresh function
  const refreshDataSilently = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      if (searchTerm.trim()) {
        params.search = searchTerm;
      }

      if (statusFilter !== "All") {
        params.status = statusFilter;
      }

      await fetchAllContactForms(params);
    } catch (error) {
      console.error("Silent refresh failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    setSelectedIds(new Set()); // Clear selection on page change
  };

  const handleItemsPerPageChange = (newLimit) => {
    setItemsPerPage(newLimit);
    setCurrentPage(1); // Reset to first page when changing items per page
    setSelectedIds(new Set());
  };

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSearchSubmit = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const handleStatusFilterChange = (status) => {
    setStatusFilter(status);
    setShowStatusDropdown(false);
    setCurrentPage(1); // Reset to first page when changing status filter
    setSelectedIds(new Set());
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        statusDropdownRef.current &&
        !statusDropdownRef.current.contains(event.target)
      ) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isAllPageSelected =
    paginatedItems.length > 0 &&
    paginatedItems.every((s) => selectedIds.has(s.id));

  const getInitialColor = (initial) => {
    const colors = [
      "bg-blue-500",
      "bg-emerald-500",
      "bg-purple-500",
      "bg-amber-500",
      "bg-pink-500",
      "bg-indigo-500",
      "bg-cyan-500",
      "bg-orange-500",
    ];
    const index = initial.charCodeAt(0) % colors.length;
    return colors[index];
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "New":
      case "unread":
        return "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border border-blue-200 dark:border-blue-800";
      case "Replied":
        return "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800";
      case "Read":
        return "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600";
      default:
        return "bg-gray-100 text-gray-700 border border-gray-200";
    }
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearchTerm("");
    setStatusFilter("All");
    setCurrentPage(1);
    setSelectedIds(new Set());
  };

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 pb-12 relative">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-text dark:text-dark-text">
            Contact Form Submissions
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1 text-sm sm:text-base">
            Manage and respond to user inquiries.{" "}
            <span className="font-medium">
              Total: {totalItems} submission(s)
            </span>
          </p>
        </div>

        <div className="flex gap-3 sm:gap-4">
          <div className="bg-card dark:bg-dark-surface p-3 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border flex items-center gap-3 flex-1 sm:flex-none">
            <div className="bg-pink-100 dark:bg-blue-900/30 p-2 rounded-xl text-primary">
              <MessageSquareText size={18} className="sm:w-5 sm:h-5" />
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-dark-muted">
                Showing
              </p>
              <p className="text-lg font-bold text-text dark:text-dark-text">
                {totalItems} forms
              </p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border hover:bg-accent dark:hover:bg-gray-700 transition-colors"
            disabled={isLoading}
          >
            <RefreshCw
              size={20}
              className={`text-primary ${isLoading ? "animate-spin" : ""}`}
            />
            {isLoading ? "Loading..." : "Refresh"}
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border hover:bg-accent dark:hover:bg-gray-700 transition-colors"
          >
            <Filter size={20} className="text-primary" />
            {showFilters ? "Hide Filters" : "Show Filters"}
          </button>
        </div>
      </div>

      {/* Filter Bar */}
      {showFilters && (
        <div className="bg-card dark:bg-dark-surface p-1.5 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-border mb-6 flex gap-2">
          {/* Search Input */}
          <div className="relative flex-1">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search by name, email, or subject..."
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyPress={handleSearchKeyPress}
              className="w-full pl-12 pr-4 py-3 rounded-xl bg-accent dark:bg-gray-900 border border-transparent focus:bg-card dark:focus:bg-dark-surface focus:border-primary outline-none text-sm transition-all text-text dark:text-dark-text placeholder-gray-400"
            />
          </div>

          {/* Search Button */}
          <button
            onClick={handleSearchSubmit}
            className="flex items-center justify-center px-4 py-3 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-medium transition-colors"
          >
            <Search size={16} />
          </button>

          {/* Status Filter Dropdown */}
          <div className="relative" ref={statusDropdownRef}>
            <button
              onClick={() => setShowStatusDropdown(!showStatusDropdown)}
              className="flex items-center gap-2 px-4 py-3 bg-accent dark:bg-gray-900 text-gray-600 dark:text-dark-muted rounded-xl text-sm font-medium border border-transparent hover:border-gray-200 dark:hover:border-dark-border transition-colors whitespace-nowrap"
            >
              <Filter size={16} />
              {statusFilter === "All"
                ? "All Statuses"
                : statusFilter === "unread"
                  ? "New"
                  : statusFilter}
              <ChevronDown
                size={14}
                className={`transition-transform ${
                  showStatusDropdown ? "rotate-180" : ""
                }`}
              />
            </button>

            {showStatusDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-card dark:bg-dark-surface rounded-xl shadow-xl border border-gray-100 dark:border-dark-border p-1 z-50 animate-in fade-in zoom-in-95 duration-200">
                {[
                  { value: "All", label: "All Statuses" },
                  { value: "unread", label: "New" },
                  { value: "Read", label: "Read" },
                  { value: "Replied", label: "Replied" },
                ].map((status) => (
                  <button
                    key={status.value}
                    onClick={() => handleStatusFilterChange(status.value)}
                    className={`w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                      statusFilter === status.value
                        ? "bg-primary text-white dark:bg-primary"
                        : "text-gray-600 dark:text-dark-muted hover:bg-accent dark:hover:bg-gray-800"
                    }`}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Reset Button */}
          <button
            onClick={resetFilters}
            className="flex items-center justify-center px-4 py-3 rounded-xl bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
            title="Reset Filters"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      )}

      {/* Main Content Area */}
      <div className="bg-card dark:bg-dark-surface rounded-3xl shadow-sm border border-gray-100 dark:border-dark-border overflow-hidden">
        {/* Loading State */}
        {isLoading && (
          <div className="absolute inset-0 bg-white/50 dark:bg-black/50 z-10 flex items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Selection Actions Bar */}
        {selectedIds.size > 0 && (
          <div className="px-4 sm:px-6 py-3 bg-gradient-to-r from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/5 border-b border-primary/20 dark:border-primary/10 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="text-sm font-bold text-primary dark:text-primary">
                {selectedIds.size} submission(s) selected
              </span>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleOpenEmailModal}
                className="px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-colors flex items-center gap-2 text-sm shadow-lg shadow-primary/20 dark:shadow-none"
              >
                <Mail size={16} /> Compose Reply
              </button>
            </div>
          </div>
        )}

        {/* Table - Horizontal scroll on small screens */}
        <div className="overflow-x-auto min-h-[400px]">
          <table className="w-full text-sm text-left min-w-[800px]">
            <thead className="text-xs text-gray-500 uppercase bg-gray-50 dark:bg-gray-900/50 border-b border-gray-100 dark:border-gray-700">
              <tr>
                <th className="px-4 sm:px-6 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={isAllPageSelected}
                    onChange={handleSelectAll}
                    className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300 cursor-pointer"
                  />
                </th>
                <th className="px-4 sm:px-6 py-3">Submitter</th>
                <th className="px-4 sm:px-6 py-3">Contact</th>
                <th className="px-4 sm:px-6 py-3">Subject</th>
                <th className="px-4 sm:px-6 py-3">Date</th>
                <th className="px-4 sm:px-6 py-3 text-center">Status</th>
                <th className="px-4 sm:px-6 py-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {paginatedItems.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted"
                  >
                    No submissions found matching your search.
                    <div className="mt-2 text-sm">
                      <button
                        onClick={resetFilters}
                        className="text-primary hover:underline"
                      >
                        Clear filters
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedItems.map((item) => (
                  <React.Fragment key={item.id}>
                    <tr
                      className={`hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors ${
                        selectedIds.has(item.id)
                          ? "bg-primary/5 dark:bg-primary/10"
                          : ""
                      } ${
                        item.status === "New" || item.status === "unread"
                          ? "bg-blue-50/30 dark:bg-blue-900/10"
                          : ""
                      }`}
                    >
                      <td
                        className="px-4 sm:px-6 py-3"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={selectedIds.has(item.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectOne(item.id, e);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 rounded text-primary focus:ring-primary border-gray-300 cursor-pointer"
                        />
                      </td>
                      <td className="px-4 sm:px-6 py-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`${getInitialColor(
                              item.initial,
                            )} w-8 h-8 sm:w-9 sm:h-9 rounded-full flex items-center justify-center text-white font-bold text-sm`}
                          >
                            {item.initial}
                          </div>
                          <p
                            className={`text-sm ${
                              item.status === "New" || item.status === "unread"
                                ? "font-bold text-text dark:text-dark-text"
                                : "font-medium text-gray-700 dark:text-dark-muted"
                            }`}
                          >
                            {item.name}
                          </p>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-3">
                        <div className="flex flex-col text-xs text-gray-500 dark:text-dark-muted">
                          <span className="flex items-center gap-1">
                            <Mail size={10} /> {item.email}
                          </span>
                          <span className="flex items-center gap-1 mt-0.5">
                            <Phone size={10} /> {item.phone}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-3">
                        <p
                          className={`text-sm ${
                            item.status === "New" || item.status === "unread"
                              ? "font-bold text-text dark:text-dark-text"
                              : "text-gray-600 dark:text-dark-muted"
                          }`}
                        >
                          {item.subject}
                        </p>
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-gray-500 dark:text-dark-muted text-xs">
                        {item.date}
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-center">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-bold ${getStatusBadge(
                            item.status,
                          )}`}
                        >
                          {item.status}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-center">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleMessage(item.id);
                          }}
                          className="text-gray-400 hover:text-primary dark:hover:text-primary transition-colors flex items-center justify-center mx-auto gap-1 text-xs font-medium"
                        >
                          {expandedIds.has(item.id) ? (
                            <>
                              Hide <EyeOff size={16} />
                            </>
                          ) : (
                            <>
                              View <Eye size={16} />
                            </>
                          )}
                        </button>
                      </td>
                    </tr>
                    {expandedIds.has(item.id) && (
                      <tr className="bg-gray-50 dark:bg-gray-900/40">
                        <td colSpan={7} className="px-4 sm:px-6 py-6">
                          <div className="max-w-3xl mx-auto bg-card dark:bg-dark-surface rounded-xl p-4 sm:p-6 border border-gray-100 dark:border-dark-border shadow-sm">
                            <h4 className="text-sm font-bold text-gray-500 dark:text-dark-muted uppercase mb-2 flex items-center gap-2">
                              <MessageSquareText size={16} /> Message Content
                            </h4>
                            <p className="text-text dark:text-dark-text whitespace-pre-wrap leading-relaxed text-sm">
                              {item.message}
                            </p>
                            <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row justify-end gap-3">
                              {(item.status === "New" ||
                                item.status === "unread") && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleMarkAsRead(item.id);
                                  }}
                                  className="px-3 sm:px-4 py-2 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 rounded-lg text-sm font-medium hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors flex items-center justify-center gap-2"
                                >
                                  <CheckCircle size={16} /> Mark as Read
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedIds(new Set([item.id]));
                                  handleOpenEmailModal();
                                }}
                                className="px-3 sm:px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2"
                              >
                                <Mail size={16} /> Reply Directly
                              </button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="border-t border-gray-100 dark:border-dark-border bg-gray-50/50 dark:bg-dark-surface p-2">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            onItemsPerPageChange={handleItemsPerPageChange}
            totalItems={totalItems}
          />
        </div>
      </div>

      {/* Email Composer Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-3xl animate-in fade-in zoom-in-95 duration-200 overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-4 sm:p-6 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center">
              <h3 className="text-lg font-bold text-text dark:text-dark-text flex items-center gap-2">
                <Mail size={20} className="text-primary" /> Compose Reply
              </h3>
              <button
                onClick={() => setShowEmailModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 sm:p-6 space-y-4 flex-1 overflow-y-auto">
              {/* Recipient Info */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-500 dark:text-dark-muted font-medium">
                  To:
                </span>
                <span className="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full text-xs font-bold">
                  {selectedIds.size} Recipients Selected
                </span>
              </div>

              <div>
                <input
                  type="text"
                  placeholder="Subject"
                  value={emailSubject}
                  onChange={(e) => setEmailSubject(e.target.value)}
                  className="w-full px-4 py-3 text-lg font-bold border-b border-gray-200 dark:border-gray-700 bg-transparent outline-none placeholder-gray-400 dark:text-dark-text focus:border-primary transition-colors"
                />
              </div>

              {/* Custom Text Editor */}
              <div className="mb-12 border border-gray-200 dark:border-dark-border rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary transition-all">
                {/* Toolbar */}
                <div className="flex gap-1 p-2 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-dark-border overflow-x-auto">
                  <button
                    onClick={() => applyFormatting("bold")}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-colors"
                    type="button"
                    title="Bold (Markdown: **text**)"
                  >
                    <Bold size={16} />
                  </button>
                  <button
                    onClick={() => applyFormatting("italic")}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-colors"
                    type="button"
                    title="Italic (Markdown: *text*)"
                  >
                    <Italic size={16} />
                  </button>
                  <button
                    onClick={() => applyFormatting("underline")}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-colors"
                    type="button"
                    title="Underline (Markdown: __text__)"
                  >
                    <Underline size={16} />
                  </button>
                  <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1 flex-shrink-0"></div>
                  <button
                    onClick={() => {
                      const textarea = textareaRef.current;
                      if (!textarea) return;
                      const start = textarea.selectionStart;
                      const newBody =
                        emailBody.substring(0, start) +
                        "\n\n" +
                        emailBody.substring(start);
                      setEmailBody(newBody);
                      setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(start + 2, start + 2);
                      }, 0);
                    }}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-colors"
                    type="button"
                    title="Align Left (New Paragraph)"
                  >
                    <AlignLeft size={16} />
                  </button>
                  <button
                    onClick={() => {
                      const textarea = textareaRef.current;
                      if (!textarea) return;
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const selectedText = emailBody.substring(start, end);
                      if (!selectedText) {
                        toast.info("Please select text to center");
                        return;
                      }
                      const centered = `<center>${selectedText}</center>`;
                      const newBody =
                        emailBody.substring(0, start) +
                        centered +
                        emailBody.substring(end);
                      setEmailBody(newBody);
                      setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(
                          start + centered.length,
                          start + centered.length,
                        );
                      }, 0);
                    }}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-colors"
                    type="button"
                    title="Center Text (HTML: <center>)"
                  >
                    <AlignCenter size={16} />
                  </button>
                  <button
                    onClick={() => {
                      const textarea = textareaRef.current;
                      if (!textarea) return;
                      const start = textarea.selectionStart;
                      const end = textarea.selectionEnd;
                      const selectedText = emailBody.substring(start, end);
                      if (!selectedText) {
                        toast.info("Please select text to align right");
                        return;
                      }
                      const aligned = `<div style="text-align: right;">${selectedText}</div>`;
                      const newBody =
                        emailBody.substring(0, start) +
                        aligned +
                        emailBody.substring(end);
                      setEmailBody(newBody);
                      setTimeout(() => {
                        textarea.focus();
                        textarea.setSelectionRange(
                          start + aligned.length,
                          start + aligned.length,
                        );
                      }, 0);
                    }}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-colors"
                    type="button"
                    title="Align Right (HTML)"
                  >
                    <AlignRight size={16} />
                  </button>
                  <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1 flex-shrink-0"></div>
                  <button
                    onClick={() => applyFormatting("list")}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-colors"
                    type="button"
                    title="Bullet List"
                  >
                    <List size={16} />
                  </button>
                  <button
                    onClick={() => applyFormatting("link")}
                    className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 flex-shrink-0 transition-colors"
                    type="button"
                    title="Insert Link (Markdown: [text](url))"
                  >
                    <LinkIcon size={16} />
                  </button>
                </div>
                <textarea
                  ref={textareaRef}
                  value={emailBody}
                  onChange={(e) => setEmailBody(e.target.value)}
                  className="w-full h-48 sm:h-64 p-4 bg-transparent outline-none resize-none text-text dark:text-dark-text leading-relaxed placeholder-gray-400"
                  placeholder="Write your response here..."
                />
              </div>

              {emailError && (
                <p className="text-red-500 text-sm font-medium animate-in fade-in mt-4">
                  {emailError}
                </p>
              )}
            </div>

            <div className="p-4 sm:p-6 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30 flex flex-col sm:flex-row justify-end gap-3">
              <button
                onClick={() => setShowEmailModal(false)}
                className="px-4 sm:px-6 py-2.5 bg-white border border-gray-200 dark:bg-gray-800 dark:border-dark-border text-text dark:text-dark-text rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm sm:text-base"
              >
                Discard
              </button>
              <button
                onClick={handleSendEmail}
                disabled={isSending}
                className="px-6 sm:px-8 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold transition-colors shadow-lg shadow-primary/20 dark:shadow-none flex items-center justify-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed text-sm sm:text-base"
              >
                {isSending ? (
                  <>Sending...</>
                ) : (
                  <>
                    Send & Mark as Replied <Send size={16} />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactUs;
